import React, { useCallback, useContext, useEffect, useRef } from 'react'
// import { getSrc } from 'gatsby-plugin-image'
import PropTypes from 'prop-types'
import { isMobile } from 'react-device-detect'
import { StoreDispatch, StoreState } from '../../Store'

// Components
import Button from '../Button'
import ContainedWrap from '../ContainedWrap'
import Container from '../Container'
import ExpeditionRoute from '../ExpeditionRoute'
import Grid from '../Grid'
import GridItem from '../GridItem'
import PageTransitionLink from '../PageTransitionLink'
import RichText from '../RichText'
import ScrollSection from '../ScrollSection'
import Spacer from '../Spacer'
import Video from '../Video'
import AnimateScaleIn from '../animation/AnimateScaleIn'
import AnimateSlideText from '../animation/AnimateSlideText'

// Vars
import { Label, TextBody } from '../TextStyles'

// Styles
import { colors } from '../../styles/vars/colors.style'
import {
  ExpeditionHeroIntro,
  ExpeditionHeroIntroText,
  ExpeditionHeroMain,
  ExpeditionHeroTitle,
  ExpeditionHeroVimeo,
} from './index.style'

const ExpeditionHero = ({
  id,
  backgroundColor,
  labelColor,
  expeditionDates,
  expeditionNumber,
  title,
  heading,
  videoUrl,
  placeholder,
  text,
  button,
}) => {
  const maxDelayTimeout = useRef(null)

  const store = useContext(StoreState)
  const dispatch = useContext(StoreDispatch)

  const { waiting } = store

  const videoIsReady = useCallback(() => {
    if (maxDelayTimeout.current) clearTimeout(maxDelayTimeout.current)
    dispatch({ type: 'END_INITIAL_WAIT' })
    dispatch({ type: 'HIDE_TRANSITION_MASK' })
    setTimeout(window.scroll.update, 750)
  }, [dispatch, maxDelayTimeout])

  const initialDelay = 2

  useEffect(() => {
    dispatch({ type: 'BEGIN_INITIAL_WAIT' })
    maxDelayTimeout.current = setTimeout(videoIsReady, 1000)

    return () => {
      if (maxDelayTimeout.current) clearTimeout(maxDelayTimeout.current)
    }
  }, [dispatch, maxDelayTimeout, videoIsReady])

  // const placeholderSource = getSrc(placeholder.gatsbyImageData)

  return (
    <ScrollSection>
      <ExpeditionHeroMain bgColor={backgroundColor}>
        <ExpeditionRoute
          id={`${id}-routes`}
          backgroundColor={backgroundColor}
          labelColor={labelColor}
          expeditionNumber={expeditionNumber}
        />
        <Container>
          <Grid>
            <GridItem
              tabletL={expeditionNumber === '2' ? 6 : 6}
              desk={expeditionNumber === '2' ? 6 : 6}
              deskStart={2}
            >
              <ExpeditionHeroIntro>
                <Spacer size={[100, 179]} />
                <AnimateSlideText
                  id={`${id}-dates`}
                  delay={initialDelay}
                  useInView={false}
                  isInView={!waiting}
                >
                  <Label color={colors.light} bgColor={labelColor}>
                    {expeditionDates}
                  </Label>
                </AnimateSlideText>
                <Spacer size={[10]} />
                <AnimateSlideText
                  id={`${id}-title`}
                  delay={initialDelay + 0.1}
                  useInView={false}
                  isInView={!waiting}
                >
                  <ExpeditionHeroTitle>
                    <TextBody>
                      <span dangerouslySetInnerHTML={{ __html: title }}></span>
                    </TextBody>
                  </ExpeditionHeroTitle>
                </AnimateSlideText>
                <Spacer size={[20, 57]} />
                <ContainedWrap size={800} center={false}>
                  <RichText
                    id={`${id}-heading`}
                    content={heading}
                    delay={initialDelay + 1}
                    upgradeHeading
                    forceHeading
                    hasHeadingOne={true}
                    useInView={false}
                    isInView={!waiting}
                  />
                </ContainedWrap>
                <Spacer size={[60, 100]} />
              </ExpeditionHeroIntro>
            </GridItem>
          </Grid>
          <ExpeditionHeroVimeo
            visible={!waiting}
            delay={isMobile ? initialDelay + 1.2 : 3}
          >
            <AnimateScaleIn
              id={`${id}-video`}
              delay={isMobile ? initialDelay + 1.2 : 0}
              duration={2.4}
              opacity={0}
              position={`relative`}
              pointerEvents={``}
              syncWithScroll={!isMobile}
              useInView={!isMobile}
              isInView={isMobile ? !waiting : null}
            >
              <Video src={videoUrl} playInView={false} />
            </AnimateScaleIn>
          </ExpeditionHeroVimeo>
          {text && (
            <Grid>
              <GridItem desk={10} deskStart={2}>
                <Spacer size={[50]} />
                <AnimateSlideText id={`${id}-text`}>
                  <ExpeditionHeroIntroText>
                    {text.introText}
                  </ExpeditionHeroIntroText>
                </AnimateSlideText>
                {button && (
                  <>
                    <Spacer size={[30, 40]} />
                    <AnimateSlideText id={`${id}-cta`}>
                      <PageTransitionLink
                        to={`/${button.internalLink.slug}${
                          button.anchorIdOptional
                            ? `?` + button.anchorIdOptional
                            : ``
                        }`}
                        mask={false}
                      >
                        <Button
                          variant="underline"
                          color={colors.darkBlue}
                          inline
                        >
                          {button.label}
                        </Button>
                      </PageTransitionLink>
                    </AnimateSlideText>
                  </>
                )}
              </GridItem>
            </Grid>
          )}
          <Spacer size={[119]} />
        </Container>
      </ExpeditionHeroMain>
    </ScrollSection>
  )
}

ExpeditionHero.propTypes = {
  id: PropTypes.string.isRequired,
  backgroundColor: PropTypes.string,
  labelColor: PropTypes.string,
  expeditionDates: PropTypes.string,
  expeditionNumber: PropTypes.string,
  title: PropTypes.string,
  heading: PropTypes.object,
  placeholder: PropTypes.object,
  videoUrl: PropTypes.string,
  text: PropTypes.object,
  button: PropTypes.object,
}

export default ExpeditionHero
